import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PreventiviPort } from './preventivi.port';
import {
  Preventivo,
  CreatePreventivoDto,
  UpdatePreventivoDto,
  PaginatedResponse,
  PreventiviQueryParams, DocumentoPreventivo,
} from '../../models/preventivi.model';
import { environment } from '../../../environments/environment';
import moment from 'moment';
import {TenantService} from '../tenant.service';
import {Capitolo} from '../../models/capitoli.model';

@Injectable({ providedIn: 'root' })
export class PreventiviHttpService implements PreventiviPort {
  private http = inject(HttpClient);
  private tenantService = inject(TenantService);
  private base = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/preventivi`;

  getList(queryParams?: PreventiviQueryParams): Observable<PaginatedResponse<Preventivo>> {
    let params = new HttpParams();

    if (queryParams) {
      if (queryParams.size != null) params = params.set('size', queryParams.size.toString());
      if (queryParams.page != null) params = params.set('page', queryParams.page.toString());

      if (queryParams.wordSearch) params = params.set('wordSearch', queryParams.wordSearch);
      if (queryParams.idCliente != null) params = params.set('idCliente', queryParams.idCliente.toString());
      if (queryParams.idCantiere != null) params = params.set('idCantiere', queryParams.idCantiere.toString());
      if (queryParams.dateStart) params = params.set('dateStart', queryParams.dateStart);
      if (queryParams.dateEnd) params = params.set('dateEnd', queryParams.dateEnd);
      if (queryParams.stato) params = params.set('stato', queryParams.stato);
      if (queryParams.archiviato != null) params = params.set('archiviato', String(queryParams.archiviato));

      // sort (Spring style): sort=campo,asc&sort=campo2,desc
      if (Array.isArray(queryParams.sort) && queryParams.sort.length) {
        queryParams.sort.forEach((s) => {
          const v = (s ?? '').trim();
          if (!v) return;
          params = params.append('sort', v);
        });
      }
    }

    return this.http.get<PaginatedResponse<Preventivo>>(this.base, { params });
  }

  getById(id: number): Observable<Preventivo> {
    return this.http.get<Preventivo>(`${this.base}/${id}`);
  }

  create(dto: CreatePreventivoDto): Observable<number> {
    return this.http.post<number>(this.base, dto);
  }

  update(id: number, changes: UpdatePreventivoDto): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}`, changes);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  archive(id: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/archive`, null, { params: { archivia: 'true' } });
  }

  unarchive(id: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/archive`, null, { params: { archivia: 'false' } });
  }

  setAsRefused(id: number, notaRifiuto?: string, dataRifiutato?:any) {
    const params: any = {
      refused: true,
    };

    if (notaRifiuto) {
      params.notaRifiuto = notaRifiuto;
      params.dataRifiutato = moment(dataRifiutato).format('YYYY-MM-DD')
    }

    return this.http.patch<number>(`${this.base}/${id}/setRefused`, null, {
      params,
    });
  }

  associateTo(id: number, idCantiere: number, dataAccettato: string): Observable<number> {
    const params = new HttpParams()
      .set('idCantiere', idCantiere.toString())
      .set('dataAccettato', dataAccettato);
    return this.http.patch<number>(`${this.base}/${id}/associate`, null, { params });
  }

  duplicate(id: number, idCliente: number): Observable<number> {
    const params = new HttpParams().set('idCliente', idCliente.toString());
    return this.http.post<number>(`${this.base}/${id}/duplicate`, null, { params });
  }

  getDocumentiByPreventivo(
    idPreventivo: number
  ): Observable<DocumentoPreventivo[]> {

    return this.http.get<DocumentoPreventivo[]>(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/documenti-preventivo/byIdPreventivo/${idPreventivo}`
    );
  }

  createDocumentoPreventivo(
    idPreventivo: number,
    file: File,
    descrizione?: string
  ): Observable<number> {

    const formData = new FormData();
    formData.append('file', file);

    let params = new HttpParams()
      .set('idPreventivo', idPreventivo);

    if (descrizione) {
      params = params.set('descrizione', descrizione);
    }

    return this.http.post<number>(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/documenti-preventivo`,
      formData,
      { params }
    );
  }

  updateDocumentoPreventivo(
    id: number,
    descrizione: string
  ): Observable<number> {
    return this.http.patch<number>(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/documenti-preventivo/${id}`,
      { descrizione }
    );
  }

  deleteDocumentoPreventivo(
    id: number
  ): Observable<void> {

    return this.http.delete<void>(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/documenti-preventivo/${id}`
    );
  }

  previewFile(fileId: number): Observable<Blob> {
    return this.http.get(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/files/${fileId}/preview`,
      {
        responseType: 'blob',
      }
    );
  }

  getCapitoliByPreventivo(idPreventivo: number): Observable<Capitolo[]> {
    return this.http.get<Capitolo[]>(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/capitoli`,
      { params: new HttpParams().set('preventivoId', idPreventivo) }
    );
  }

  moveCapitolo(id: number, nuovoOrdine: number): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiBaseUrl}/${this.tenantService.tenant}/capitoli/${id}/move`,
      null,
      { params: new HttpParams().set('nuovoOrdine', nuovoOrdine) }
    );
  }

  getSuggestionsCapitoli(
    nomeSearch: string,
    page = 0,
    size = 10
  ): Observable<PaginatedResponse<Capitolo>> {
    const params = new HttpParams()
      .set('nomeSearch', nomeSearch)
      .set('page', page)
      .set('size', size);

    return this.http.get<PaginatedResponse<Capitolo>>(
      `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/capitoli/suggestions`,
      { params }
    );
  }
}
